import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Calendar } from "./components/ui/calendar";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { MapPin, Phone, Mail, Clock, Users, Trophy, Star, Globe } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    title: "Warsaw Royal Cricket Club",
    subtitle: "Promoting Cricket Excellence in the Heart of Warsaw",
    navigation: {
      home: "Home",
      about: "About",
      booking: "Book Net Session",
      membership: "Join Club", 
      news: "News & Events",
      gallery: "Gallery",
      players: "Team Stats",
      contact: "Contact"
    },
    hero: {
      title: "Welcome to Warsaw Royal Cricket Club",
      subtitle: "Premier Cricket Club in Warsaw, Poland",
      description: "Experience the thrill of cricket in Poland's capital. Join our vibrant community of cricket enthusiasts.",
      cta: "Book Your Session"
    },
    about: {
      title: "About Our Club",
      description: "Warsaw Royal Cricket Club is a proud member of PFK (Polska Federacja Krykieta), which is affiliated with PKOL (Polski Komitet Olimpijski). We are dedicated to promoting cricket excellence in Warsaw and throughout Poland.",
      mission: "Our Mission",
      missionText: "To develop cricket talent, foster community spirit, and represent Poland with pride in international competitions."
    },
    booking: {
      title: "Book Net Practice Session",
      name: "Full Name",
      email: "Email Address",
      phone: "Phone Number",
      date: "Select Date",
      time: "Select Time",
      duration: "Duration (hours)",
      notes: "Additional Notes",
      submit: "Book Session"
    },
    membership: {
      title: "Join Warsaw Royal Cricket Club",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email Address",
      phone: "Phone Number",
      dateOfBirth: "Date of Birth",
      type: "Membership Type",
      types: {
        junior: "Junior (Under 18)",
        senior: "Senior (18+)",
        student: "Student",
        family: "Family Package"
      },
      emergencyName: "Emergency Contact Name",
      emergencyPhone: "Emergency Contact Phone",
      experience: "Cricket Experience",
      notes: "Additional Notes",
      submit: "Apply for Membership"
    },
    contact: {
      title: "Get in Touch",
      address: "Fort Wola 22, Warsaw, Poland",
      ground: "PFK Oval Cricket Ground Wola"
    }
  },
  pl: {
    title: "Warszawski Królewki Klub Krykieta",
    subtitle: "Promujemy Doskonałość Krykieta w Sercu Warszawy",
    navigation: {
      home: "Strona Główna",
      about: "O Nas",
      booking: "Rezerwuj Sesję",
      membership: "Dołącz do Klubu",
      news: "Aktualności",
      gallery: "Galeria",
      players: "Statystyki Drużyny",
      contact: "Kontakt"
    },
    hero: {
      title: "Witamy w Warszawskim Królewskim Klubie Krykieta",
      subtitle: "Wiodący Klub Krykieta w Warszawie, Polska",
      description: "Doświadcz emocji krykieta w stolicy Polski. Dołącz do naszej żywej społeczności miłośników krykieta.",
      cta: "Zarezerwuj Sesję"
    },
    about: {
      title: "O Naszym Klubie",
      description: "Warszawski Królewski Klub Krykieta jest dumnym członkiem PFK (Polska Federacja Krykieta), która jest afiliowana z PKOL (Polski Komitet Olimpijski). Jesteśmy oddani promowaniu doskonałości krykieta w Warszawie i całej Polsce.",
      mission: "Nasza Misja",
      missionText: "Rozwijać talent krykietowy, wspierać ducha społeczności i reprezentować Polskę z dumą w międzynarodowych zawodach."
    },
    booking: {
      title: "Zarezerwuj Sesję Treningową",
      name: "Imię i Nazwisko",
      email: "Adres Email",
      phone: "Numer Telefonu", 
      date: "Wybierz Datę",
      time: "Wybierz Godzinę",
      duration: "Czas Trwania (godziny)",
      notes: "Dodatkowe Uwagi",
      submit: "Zarezerwuj Sesję"
    },
    membership: {
      title: "Dołącz do Warszawskiego Królewskiego Klubu Krykieta",
      firstName: "Imię",
      lastName: "Nazwisko",
      email: "Adres Email",
      phone: "Numer Telefonu",
      dateOfBirth: "Data Urodzenia",
      type: "Typ Członkostwa",
      types: {
        junior: "Junior (Poniżej 18 lat)",
        senior: "Senior (18+)",
        student: "Student",
        family: "Pakiet Rodzinny"
      },
      emergencyName: "Kontakt Awaryjny - Imię",
      emergencyPhone: "Kontakt Awaryjny - Telefon",
      experience: "Doświadczenie w Krykiecie",
      notes: "Dodatkowe Uwagi",
      submit: "Złóż Podanie"
    },
    contact: {
      title: "Skontaktuj się z Nami",
      address: "Fort Wola 22, Warszawa, Polska",
      ground: "Boisko Krykietowe PFK Oval Wola"
    }
  }
};

const Navigation = ({ language, setLanguage, t, activeSection, setActiveSection }) => {
  return (
    <nav className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Trophy className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t.title}</h1>
              <p className="text-xs text-gray-600">{t.subtitle}</p>
            </div>
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            {Object.entries(t.navigation).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`text-sm font-medium transition-colors hover:text-green-600 ${
                  activeSection === key ? 'text-green-600' : 'text-gray-700'
                }`}
                data-testid={`nav-${key}`}
              >
                {label}
              </button>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLanguage(language === 'en' ? 'pl' : 'en')}
              data-testid="language-toggle"
            >
              <Globe className="h-4 w-4 mr-1" />
              {language === 'en' ? 'PL' : 'EN'}
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

const HeroSection = ({ t }) => {
  return (
    <div className="relative h-screen flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1512719994953-eabf50895df7')`,
        }}
      />
      <div className="absolute inset-0 bg-black/40" />
      
      <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
        <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
          {t.hero.title}
        </h1>
        <p className="text-xl md:text-2xl mb-4 opacity-90">
          {t.hero.subtitle}
        </p>
        <p className="text-lg mb-8 opacity-80 max-w-2xl mx-auto">
          {t.hero.description}
        </p>
        <Button 
          size="lg" 
          className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg"
          data-testid="hero-cta-button"
        >
          {t.hero.cta}
        </Button>
      </div>
    </div>
  );
};

const AboutSection = ({ t }) => {
  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">{t.about.title}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t.about.description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1593341646782-e0b495cff86d"
              alt="Cricket Action"
              className="rounded-lg shadow-lg w-full h-80 object-cover"
            />
          </div>
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{t.about.mission}</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              {t.about.missionText}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-white rounded-lg shadow">
                <Users className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">150+</div>
                <div className="text-sm text-gray-600">Members</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg shadow">
                <Trophy className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-900">25+</div>
                <div className="text-sm text-gray-600">Trophies</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const BookingSection = ({ t }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: null,
    time: '',
    duration: '1',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.date) {
        toast.error('Please select a date');
        return;
      }

      const bookingData = {
        ...formData,
        date: formData.date.toISOString().split('T')[0],
        time: formData.time + ':00'
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking submitted successfully! We will contact you for confirmation.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        date: null,
        time: '',
        duration: '1',
        notes: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    }
  };

  return (
    <div className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">{t.booking.title}</h2>
          <p className="text-lg text-gray-600">Reserve your practice time at our world-class facilities</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Net Practice Booking</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name">{t.booking.name}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="booking-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{t.booking.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="booking-email-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">{t.booking.phone}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="booking-phone-input"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <Label>{t.booking.date}</Label>
                  <Calendar
                    mode="single"
                    selected={formData.date}
                    onSelect={(date) => setFormData({...formData, date})}
                    className="rounded-md border"
                    disabled={(date) => date < new Date()}
                    data-testid="booking-date-calendar"
                  />
                </div>
                <div>
                  <Label htmlFor="time">{t.booking.time}</Label>
                  <Select onValueChange={(value) => setFormData({...formData, time: value})} value={formData.time}>
                    <SelectTrigger data-testid="booking-time-select">
                      <SelectValue placeholder="Select time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="time-09:00" value="09:00">09:00 AM</SelectItem>
                      <SelectItem key="time-10:00" value="10:00">10:00 AM</SelectItem>
                      <SelectItem key="time-11:00" value="11:00">11:00 AM</SelectItem>
                      <SelectItem key="time-14:00" value="14:00">02:00 PM</SelectItem>
                      <SelectItem key="time-15:00" value="15:00">03:00 PM</SelectItem>
                      <SelectItem key="time-16:00" value="16:00">04:00 PM</SelectItem>
                      <SelectItem key="time-17:00" value="17:00">05:00 PM</SelectItem>
                      <SelectItem key="time-18:00" value="18:00">06:00 PM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="duration">{t.booking.duration}</Label>
                  <Select onValueChange={(value) => setFormData({...formData, duration: value})} value={formData.duration}>
                    <SelectTrigger data-testid="booking-duration-select">
                      <SelectValue placeholder="1 hour" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="duration-1" value="1">1 Hour</SelectItem>
                      <SelectItem key="duration-2" value="2">2 Hours</SelectItem>
                      <SelectItem key="duration-3" value="3">3 Hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="notes">{t.booking.notes}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any special requirements or notes..."
                  data-testid="booking-notes-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                data-testid="booking-submit-button"
              >
                {t.booking.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const MembershipSection = ({ t }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: null,
    membershipType: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    cricketExperience: '',
    notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.dateOfBirth) {
        toast.error('Please select your date of birth');
        return;
      }

      const membershipData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.dateOfBirth.toISOString().split('T')[0],
        membership_type: formData.membershipType,
        emergency_contact_name: formData.emergencyContactName,
        emergency_contact_phone: formData.emergencyContactPhone,
        cricket_experience: formData.cricketExperience,
        notes: formData.notes
      };

      await axios.post(`${API}/memberships`, membershipData);
      toast.success('Membership application submitted successfully! We will review and contact you soon.');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: null,
        membershipType: '',
        emergencyContactName: '',
        emergencyContactPhone: '',
        cricketExperience: '',
        notes: ''
      });
    } catch (error) {
      console.error('Membership error:', error);
      toast.error('Failed to submit application. Please try again.');
    }
  };

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">{t.membership.title}</h2>
          <p className="text-lg text-gray-600">Become part of Warsaw's premier cricket community</p>
        </div>

        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl text-center">Membership Application</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="membership-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName">{t.membership.firstName}</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    required
                    data-testid="membership-firstname-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">{t.membership.lastName}</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    required
                    data-testid="membership-lastname-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email">{t.membership.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="membership-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">{t.membership.phone}</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    data-testid="membership-phone-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label>{t.membership.dateOfBirth}</Label>
                  <Calendar
                    mode="single"
                    selected={formData.dateOfBirth}
                    onSelect={(date) => setFormData({...formData, dateOfBirth: date})}
                    className="rounded-md border"
                    disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
                    data-testid="membership-dob-calendar"
                  />
                </div>
                <div>
                  <Label htmlFor="membershipType">{t.membership.type}</Label>
                  <Select onValueChange={(value) => setFormData({...formData, membershipType: value})} value={formData.membershipType}>
                    <SelectTrigger data-testid="membership-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem key="membership-junior" value="junior">{t.membership.types.junior}</SelectItem>
                      <SelectItem key="membership-senior" value="senior">{t.membership.types.senior}</SelectItem>
                      <SelectItem key="membership-student" value="student">{t.membership.types.student}</SelectItem>
                      <SelectItem key="membership-family" value="family">{t.membership.types.family}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="emergencyName">{t.membership.emergencyName}</Label>
                  <Input
                    id="emergencyName"
                    type="text"
                    value={formData.emergencyContactName}
                    onChange={(e) => setFormData({...formData, emergencyContactName: e.target.value})}
                    required
                    data-testid="membership-emergency-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="emergencyPhone">{t.membership.emergencyPhone}</Label>
                  <Input
                    id="emergencyPhone"
                    type="tel"
                    value={formData.emergencyContactPhone}
                    onChange={(e) => setFormData({...formData, emergencyContactPhone: e.target.value})}
                    required
                    data-testid="membership-emergency-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="experience">{t.membership.experience}</Label>
                <Textarea
                  id="experience"
                  value={formData.cricketExperience}
                  onChange={(e) => setFormData({...formData, cricketExperience: e.target.value})}
                  placeholder="Tell us about your cricket experience..."
                  required
                  data-testid="membership-experience-input"
                />
              </div>

              <div>
                <Label htmlFor="notes">{t.membership.notes}</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Any additional information..."
                  data-testid="membership-notes-input"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3"
                data-testid="membership-submit-button"
              >
                {t.membership.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const NewsSection = () => {
  const [news, setNews] = useState([]);

  useEffect(() => {
    // Sample news data - in real app this would come from API
    const sampleNews = [
      {
        id: "1",
        title: "Warsaw Royal CC Wins Regional Championship",
        title_pl: "Warszawski Królewski CC Wygrywa Mistrzostwa Regionalne", 
        type: "news",
        content: "Our team demonstrated exceptional skill and teamwork to secure the regional championship title.",
        content_pl: "Nasza drużyna wykazała się wyjątkową umiejętnością i pracą zespołową, zdobywając tytuł mistrza regionalnego.",
        author: "Club Manager",
        created_at: new Date('2024-01-15'),
        image_url: "https://images.pexels.com/photos/163465/softball-girls-team-mates-happy-163465.jpeg"
      },
      {
        id: "2",
        title: "New Practice Nets Opening Soon",
        title_pl: "Nowe Sieci Treningowe Otwierają Się Wkrótce",
        type: "event", 
        content: "State-of-the-art practice facilities will be available for all members starting next month.",
        content_pl: "Najnowocześniejsze obiekty treningowe będą dostępne dla wszystkich członków od przyszłego miesiąca.",
        author: "Facilities Manager",
        created_at: new Date('2024-01-10'),
        image_url: "https://images.unsplash.com/photo-1709134800935-d00e89d5b8e3"
      }
    ];
    setNews(sampleNews);
  }, []);

  return (
    <div className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">News & Events</h2>
          <p className="text-lg text-gray-600">Stay updated with the latest from Warsaw Royal Cricket Club</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {news.map((item) => (
            <Card key={item.id} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="aspect-video">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant={item.type === 'news' ? 'default' : 'secondary'}>
                    {item.type === 'news' ? 'News' : 'Event'}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    {item.created_at.toLocaleDateString()}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 line-clamp-3">{item.content}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const GallerySection = () => {
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API}/gallery`);
        setGallery(response.data);
      } catch (error) {
        console.error('Failed to fetch gallery:', error);
      }
    };
    fetchGallery();
  }, []);

  return (
    <div className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Gallery</h2>
          <p className="text-lg text-gray-600">Capturing moments of cricket excellence</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.map((item) => (
            <div key={item.id} className="group relative overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-shadow">
              <img
                src={item.url}
                alt={item.title}
                className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                <div className="text-center text-white p-4">
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-90">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PlayersSection = () => {
  const [players] = useState([
    {
      id: "1",
      name: "Jakub Kowalski",
      position: "Captain/All-rounder",
      matches_played: 45,
      runs_scored: 1250,
      wickets_taken: 32,
      catches: 28,
      batting_average: 35.5,
      bowling_average: 22.8,
      image_url: "https://images.unsplash.com/photo-1593766821405-f605e0f9535f"
    },
    {
      id: "2", 
      name: "Piotr Nowak",
      position: "Opening Batsman",
      matches_played: 38,
      runs_scored: 1420,
      wickets_taken: 2,
      catches: 22,
      batting_average: 42.1,
      bowling_average: 45.0,
      image_url: "https://images.unsplash.com/photo-1729027696167-0f3aa8e92e64"
    },
    {
      id: "3",
      name: "Marcin Wiśniewski", 
      position: "Fast Bowler",
      matches_played: 42,
      runs_scored: 380,
      wickets_taken: 58,
      catches: 15,
      batting_average: 12.5,
      bowling_average: 18.9,
      image_url: "https://images.pexels.com/photos/3601184/pexels-photo-3601184.jpeg"
    }
  ]);

  return (
    <div className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Team Statistics</h2>
          <p className="text-lg text-gray-600">Meet our talented squad</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {players.map((player) => (
            <Card key={player.id} className="overflow-hidden shadow-lg hover:shadow-xl transition-shadow">
              <div className="aspect-square">
                <img
                  src={player.image_url}
                  alt={player.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{player.name}</h3>
                  <p className="text-green-600 font-medium">{player.position}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900">{player.matches_played}</div>
                    <div className="text-gray-600">Matches</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900">{player.runs_scored}</div>
                    <div className="text-gray-600">Runs</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900">{player.wickets_taken}</div>
                    <div className="text-gray-600">Wickets</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900">{player.batting_average}</div>
                    <div className="text-gray-600">Avg</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

const ContactSection = ({ t }) => {
  return (
    <div className="py-20 bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-6">{t.contact.title}</h2>
          <p className="text-lg text-gray-300">Visit us at Poland's premier cricket facility</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <MapPin className="h-6 w-6 text-green-500" />
              <div>
                <h3 className="font-semibold text-lg">Location</h3>
                <p className="text-gray-300">{t.contact.address}</p>
                <p className="text-gray-300">{t.contact.ground}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Phone className="h-6 w-6 text-green-500" />
              <div>
                <h3 className="font-semibold text-lg">Phone</h3>
                <p className="text-gray-300">+48 604765550</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Mail className="h-6 w-6 text-green-500" />
              <div>
                <h3 className="font-semibold text-lg">Email</h3>
                <p className="text-gray-300">royal.krykietclub@gmail.com</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Clock className="h-6 w-6 text-green-500" />
              <div>
                <h3 className="font-semibold text-lg">Practice Hours</h3>
                <p className="text-gray-300">Monday - Friday: 9:00 AM - 8:00 PM</p>
                <p className="text-gray-300">Weekend: 8:00 AM - 6:00 PM</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-8 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Affiliations</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-green-500" />
                <span>Member of PFK (Polska Federacja Krykieta)</span>
              </div>
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-green-500" />
                <span>PKOL (Polski Komitet Olimpijski) Affiliated</span>
              </div>
              <div className="flex items-center space-x-3">
                <Star className="h-5 w-5 text-green-500" />
                <span>ICC Associate Member through PFK</span>
              </div>
            </div>
            
            <div className="mt-6 p-4 bg-green-600 rounded-lg">
              <p className="text-sm">
                "Promoting cricket excellence and Olympic values in the heart of Warsaw, Poland."
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [language, setLanguage] = useState('en');
  const [activeSection, setActiveSection] = useState('home');

  const t = translations[language];

  const renderSection = () => {
    switch (activeSection) {
      case 'booking':
        return <BookingSection t={t} />;
      case 'membership':
        return <MembershipSection t={t} />;
      case 'news':
        return <NewsSection />;
      case 'gallery':
        return <GallerySection />;
      case 'players':
        return <PlayersSection />;
      case 'contact':
        return <ContactSection t={t} />;
      case 'about':
        return <AboutSection t={t} />;
      default:
        return (
          <>
            <HeroSection t={t} />
            <AboutSection t={t} />
            <NewsSection />
            <ContactSection t={t} />
          </>
        );
    }
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Navigation 
          language={language}
          setLanguage={setLanguage}
          t={t}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
        <main data-testid={`section-${activeSection}`}>
          {renderSection()}
        </main>
        <Toaster />
      </BrowserRouter>
    </div>
  );
};

export default App;